import { zodResolver } from '@hookform/resolvers/zod';
import { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { MfaSignInForm } from '@/components/auth/mfa-sign-in-form';
import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { type SigninErrorReason, useMfaSignin, useSignin } from '@/hooks/auth';
import { emailSchema, passwordSchema } from '@/utils/schemas';

const signInSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

type SignInForm = z.infer<typeof signInSchema>;

const RATE_LIMITED_MESSAGE =
  '短時間にログインが繰り返されました。しばらく時間をおいてから再度お試しください。';

const SIGNIN_ERROR_MESSAGES: Record<SigninErrorReason, string> = {
  invalid_credentials: 'メールアドレスまたはパスワードが誤っています',
  profile_failed: 'ログインに失敗しました',
  rate_limited: RATE_LIMITED_MESSAGE,
};

export const SignInPage = () => {
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || '/';

  // MFA が有効なユーザーはログインの API が短命トークンを返すので、コード入力に切り替える
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaErrorMessage, setMfaErrorMessage] = useState<string | undefined>(undefined);

  const { mutate: signinRequest, isPending } = useSignin({
    redirectTo,
    onSuccess: () => toast.success('ログインしました'),
    onMfaRequired: (token) => {
      setMfaErrorMessage(undefined);
      setMfaToken(token);
    },
    onError: (reason) => toast.error(SIGNIN_ERROR_MESSAGES[reason]),
  });

  const { mutate: mfaSigninRequest, isPending: isMfaPending } = useMfaSignin({
    redirectTo,
    onSuccess: () => toast.success('ログインしました'),
    onError: (detail) => {
      switch (detail.reason) {
        case 'invalid_code':
          setMfaErrorMessage(detail.message);
          break;
        case 'expired':
          // 短命トークンの期限切れ。最初からやり直してもらう
          setMfaToken(null);
          toast.error('時間切れのため、もう一度ログインしてください');
          break;
        case 'rate_limited':
          toast.error(RATE_LIMITED_MESSAGE);
          break;
        default:
          toast.error('ログインに失敗しました');
      }
    },
  });

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isValid },
  } = useForm<SignInForm>({
    resolver: zodResolver(signInSchema),
  });

  const onSubmit = (data: SignInForm) => {
    signinRequest({ username: data.email, password: data.password });
  };

  return (
    <Fragment>
      <title>ログイン - アニマルオフィス</title>

      <div className="flex-1 flex items-center justify-center py-10">
        <div className="w-[400px] px-5 sm:px-0 mx-auto">
          <div className="mb-5">
            <h2>ログイン</h2>
          </div>

          {mfaToken ? (
            <MfaSignInForm
              email={getValues('email')}
              onSubmit={(mfaCode) => {
                setMfaErrorMessage(undefined);
                mfaSigninRequest({ mfaToken, mfaCode });
              }}
              onBack={() => setMfaToken(null)}
              isPending={isMfaPending}
              errorMessage={mfaErrorMessage}
              onCodeTypeChange={() => setMfaErrorMessage(undefined)}
            />
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2 mb-5">
                <div>
                  <Label required className="mb-1">
                    メールアドレス
                  </Label>
                  <input {...register('email')} type="email" />
                  <FormError message={errors.email?.message} />
                </div>

                <div>
                  <Label required className="mb-1">
                    パスワード
                  </Label>
                  <PasswordInput {...register('password')} />
                  <FormError message={errors.password?.message} />
                </div>
              </div>
              <div className="flex flex-col items-center">
                <Button type="submit" fullWidth disabled={isPending || !isValid} loading={isPending}>
                  ログインする
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Fragment>
  );
};
